import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { useEffect, useRef } from 'react';
import { getSnapStepPixels } from '../../model/selectors/getSnapStepPixels';
import { warehouseMapConstructorActions } from '../../model/slice/warehouseMapConstructorSlice';
import { WarehouseMapTool } from '../../model/types';
import { ClipboardApi } from './useClipboard';
import { PolygonDrawingApi } from './usePolygonDrawing';

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return Boolean(target.closest('.ant-select-dropdown, .ant-picker-dropdown, .ant-color-picker, .ant-color-picker-trigger'));
};

interface KeyboardShortcutsArgs {
  floorId: string;
  clipboard: ClipboardApi;
  polygonHook: Pick<PolygonDrawingApi, 'isDrawing' | 'onKeyDown'>;
  cancelNonPolygonPreview: () => void;
  isPreviewingNonPolygon: () => boolean;
  /** Set when a polygon/polygon-box is in vertex-edit mode. */
  vertexEditShapeId: Nullable<string>;
  onVertexEditCommit: () => void;
  onVertexEditCancel: () => void;
  activeTool: WarehouseMapTool;
}

export const useKeyboardShortcuts = ({
  floorId,
  clipboard,
  polygonHook,
  cancelNonPolygonPreview,
  isPreviewingNonPolygon,
  vertexEditShapeId,
  onVertexEditCommit,
  onVertexEditCancel,
  activeTool,
}: KeyboardShortcutsArgs): void => {
  const dispatch = useAppDispatch();
  const snapStepPixels = useAppSelector(getSnapStepPixels);

  // Stable refs so the single long-lived listener always reads current values.
  const floorIdRef = useRef(floorId);
  const clipboardRef = useRef(clipboard);
  const polygonHookRef = useRef(polygonHook);
  const cancelPreviewRef = useRef(cancelNonPolygonPreview);
  const isPreviewingRef = useRef(isPreviewingNonPolygon);
  const vertexEditIdRef = useRef(vertexEditShapeId);
  const onVertexCommitRef = useRef(onVertexEditCommit);
  const onVertexCancelRef = useRef(onVertexEditCancel);
  const activeToolRef = useRef(activeTool);

  useEffect(() => {
    floorIdRef.current = floorId;
  }, [floorId]);
  useEffect(() => {
    clipboardRef.current = clipboard;
  }, [clipboard]);
  useEffect(() => {
    polygonHookRef.current = polygonHook;
  }, [polygonHook]);
  useEffect(() => {
    cancelPreviewRef.current = cancelNonPolygonPreview;
  }, [cancelNonPolygonPreview]);
  useEffect(() => {
    isPreviewingRef.current = isPreviewingNonPolygon;
  }, [isPreviewingNonPolygon]);
  useEffect(() => {
    vertexEditIdRef.current = vertexEditShapeId;
  }, [vertexEditShapeId]);
  useEffect(() => {
    onVertexCommitRef.current = onVertexEditCommit;
  }, [onVertexEditCommit]);
  useEffect(() => {
    onVertexCancelRef.current = onVertexEditCancel;
  }, [onVertexEditCancel]);
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (isEditableTarget(event.target)) return;

      const isMod = event.ctrlKey || event.metaKey;
      const key = event.key;

      // ── Delete / Backspace ────────────────────────────────────────────────
      if (key === 'Delete' || key === 'Backspace') {
        // Polygon drawing consumes Backspace to pop the last vertex.
        if (polygonHookRef.current.isDrawing) {
          event.preventDefault();
          polygonHookRef.current.onKeyDown(event);
          return;
        }
        // Vertex edit mode: let the editor handle via handle focus (Alt+click).
        if (vertexEditIdRef.current) return;

        event.preventDefault();
        dispatch(warehouseMapConstructorActions.deleteSelected());
        return;
      }

      // ── Enter ─────────────────────────────────────────────────────────────
      if (key === 'Enter') {
        if (polygonHookRef.current.isDrawing) {
          event.preventDefault();
          polygonHookRef.current.onKeyDown(event);
          return;
        }
        if (vertexEditIdRef.current) {
          event.preventDefault();
          onVertexCommitRef.current();
          return;
        }
        return;
      }

      // ── Escape ────────────────────────────────────────────────────────────
      if (key === 'Escape') {
        // Vertex edit cancel.
        if (vertexEditIdRef.current) {
          onVertexCancelRef.current();
          return;
        }
        // Polygon drawing cancel.
        if (polygonHookRef.current.onKeyDown(event)) return;
        if (activeToolRef.current === WarehouseMapTool.EDIT_BACKGROUND) {
          dispatch(warehouseMapConstructorActions.changeTool(WarehouseMapTool.POINTER));
          return;
        }
        // Cancel any in-progress non-polygon shape preview (rect/circle/etc.).
        if (isPreviewingRef.current()) {
          cancelPreviewRef.current();
          return;
        }
        // Default: clear selection.
        dispatch(warehouseMapConstructorActions.unselectShapes());
        return;
      }

      // ── Shortcuts disabled during polygon drawing / vertex editing ────────
      if (polygonHookRef.current.isDrawing || vertexEditIdRef.current) return;

      if (isMod && (key === 'a' || key === 'A')) {
        event.preventDefault();
        dispatch(warehouseMapConstructorActions.selectAllShapes({ floorId: floorIdRef.current }));
        return;
      }

      if (isMod && (key === 'c' || key === 'C')) {
        event.preventDefault();
        clipboardRef.current.copy();
        return;
      }

      if (isMod && (key === 'x' || key === 'X')) {
        event.preventDefault();
        clipboardRef.current.cut();
        return;
      }

      if (isMod && (key === 'v' || key === 'V')) {
        event.preventDefault();
        clipboardRef.current.paste(floorIdRef.current);
        return;
      }

      if (isMod && (key === 'd' || key === 'D')) {
        event.preventDefault();
        dispatch(
          warehouseMapConstructorActions.duplicateSelectedShapes({
            offsetX: snapStepPixels,
            offsetY: snapStepPixels,
          }),
        );
        return;
      }

      const step = event.shiftKey ? snapStepPixels : 1;

      switch (key) {
        case 'ArrowLeft':
          event.preventDefault();
          dispatch(warehouseMapConstructorActions.nudgeSelected({ dx: -step, dy: 0 }));
          break;
        case 'ArrowRight':
          event.preventDefault();
          dispatch(warehouseMapConstructorActions.nudgeSelected({ dx: step, dy: 0 }));
          break;
        case 'ArrowUp':
          event.preventDefault();
          dispatch(warehouseMapConstructorActions.nudgeSelected({ dx: 0, dy: -step }));
          break;
        case 'ArrowDown':
          event.preventDefault();
          dispatch(warehouseMapConstructorActions.nudgeSelected({ dx: 0, dy: step }));
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [dispatch, snapStepPixels]);
};
